import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
  Button,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  MemoryRounded,
  CloudRounded,
  ApiRounded,
  VisibilityRounded,
  VisibilityOffRounded,
  RefreshRounded,
} from "@mui/icons-material";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { useAuth } from "hooks/useAuth";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";

const ENGINES = [
  {
    value: "mineru",
    label: "MinerU (default)",
    description:
      "Best structured Markdown for complex/scanned documents. Runs on private GPU; processing is asynchronous.",
    icon: <MemoryRounded />,
  },
  {
    value: "docai",
    label: "Google Document AI",
    description: "Fast synchronous OCR via Google Cloud. No key required.",
    icon: <CloudRounded />,
  },
  {
    value: "mistral",
    label: "Mistral OCR",
    description:
      "Synchronous Markdown OCR via Mistral's API. Requires your organization's API key.",
    icon: <ApiRounded />,
  },
];

/**
 * Org-admin control for choosing which backend processes uploaded documents.
 * The engine choice is stored on the org doc; the Mistral key is write-only
 * (submitted here, never read back — the UI only knows whether one is saved).
 */
const DocumentProcessingSettings = () => {
  const { userData, organization, refreshOrganizationTeamData } = useAuth();

  const savedEngine = organization?.documentProcessing?.engine || "mineru";
  const mistralKeySet = Boolean(
    organization?.documentProcessing?.mistralKeySet
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: { engine: savedEngine, mistralApiKey: "" },
  });

  const selectedEngine = watch("engine");
  const enteredKey = watch("mistralApiKey");

  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState("");
  const [saveError, setSaveError] = useState("");

  // Re-seed the form when the org data loads/changes.
  useEffect(() => {
    reset({ engine: savedEngine, mistralApiKey: "" });
  }, [reset, savedEngine, organization?.id]);

  const updateConfig = useMemo(
    () =>
      firebase
        .functions()
        .httpsCallable("organizations_updatedocumentprocessing"),
    []
  );

  // Org admins and platform global admins may change this. TeamPage is
  // admin-scoped, but guard anyway.
  const canManage =
    userData?.role === "admin" || userData?.role === "global_admin";

  // Read-only, platform-wide MinerU queue counts (admin-gated server-side).
  const getQueueCounts = useMemo(
    () =>
      firebase
        .functions()
        .httpsCallable("documentprocessing_getqueuecounts"),
    []
  );
  const [queueStats, setQueueStats] = useState(null);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState("");

  const loadQueueStats = useCallback(async () => {
    setQueueLoading(true);
    setQueueError("");
    try {
      const { data } = await getQueueCounts();
      setQueueStats(data);
    } catch (err) {
      setQueueError(err?.message || "Failed to load queue status.");
    } finally {
      setQueueLoading(false);
    }
  }, [getQueueCounts]);

  useEffect(() => {
    if (canManage) {
      loadQueueStats();
    }
  }, [canManage, loadQueueStats]);

  if (!canManage) {
    return null;
  }

  const onSubmit = async (values) => {
    setSaveError("");
    setSaveSuccess("");
    setSaving(true);
    try {
      const payload = { engine: values.engine };
      const trimmedKey = (values.mistralApiKey || "").trim();
      if (values.engine === "mistral" && trimmedKey) {
        payload.mistralApiKey = trimmedKey;
      }
      await updateConfig(payload);
      await refreshOrganizationTeamData?.();
      setSaveSuccess("Document processing settings saved.");
      // Clear the transient key field; keep the (now saved) engine selection.
      setValue("mistralApiKey", "", { shouldDirty: false });
    } catch (err) {
      setSaveError(err?.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleClearKey = async () => {
    setSaveError("");
    setSaveSuccess("");
    setSaving(true);
    try {
      await updateConfig({ engine: selectedEngine, clearMistralKey: true });
      await refreshOrganizationTeamData?.();
      setSaveSuccess("Saved Mistral key removed.");
    } catch (err) {
      setSaveError(err?.message || "Failed to remove key.");
    } finally {
      setSaving(false);
    }
  };

  const needsKey = selectedEngine === "mistral" && !mistralKeySet;

  return (
    <Box component="section" sx={{ mb: 4 }}>
      <Typography
        variant="subtitle2"
        component="h3"
        fontWeight="bold"
        gutterBottom
      >
        Document processing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose which backend converts uploaded documents into text for your
        organization's clinical references and embedded documents.
      </Typography>

      {saveSuccess && (
        <Alert
          severity="success"
          onClose={() => setSaveSuccess("")}
          sx={{ mb: 2 }}
        >
          {saveSuccess}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" onClose={() => setSaveError("")} sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Controller
          name="engine"
          control={control}
          render={({ field }) => (
            <Grid container spacing={2}>
              {ENGINES.map((engine) => (
                <Grid item xs={12} sm={4} key={engine.value}>
                  <SelectableChoiceCard
                    icon={engine.icon}
                    title={engine.label}
                    description={engine.description}
                    orientation="vertical"
                    selected={field.value === engine.value}
                    onClick={() => field.onChange(engine.value)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        />

        {selectedEngine === "mistral" && (
          <Box sx={{ mt: 3 }}>
            <Controller
              name="mistralApiKey"
              control={control}
              rules={{
                validate: (value) =>
                  needsKey && !(value || "").trim()
                    ? "A Mistral API key is required to use this engine."
                    : true,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Mistral API key"
                  type={showKey ? "text" : "password"}
                  fullWidth
                  autoComplete="off"
                  error={Boolean(errors.mistralApiKey)}
                  helperText={
                    errors.mistralApiKey?.message ||
                    (mistralKeySet
                      ? "A key is saved. Leave blank to keep it, or enter a new key to replace it."
                      : "Your key is stored securely and never shown again.")
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowKey((s) => !s)}
                          edge="end"
                          aria-label={showKey ? "Hide key" : "Show key"}
                        >
                          {showKey ? (
                            <VisibilityOffRounded />
                          ) : (
                            <VisibilityRounded />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}

        {/* Allow deleting a stored key only when Mistral is NOT the active
            choice (Mistral requires a key on file). */}
        {mistralKeySet && selectedEngine !== "mistral" && (
          <Button
            color="error"
            onClick={handleClearKey}
            disabled={saving}
            sx={{ mt: 2 }}
          >
            Remove saved Mistral key
          </Button>
        )}

        <Box sx={{ mt: 3 }}>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={saving}
            disabled={!isDirty && !enteredKey}
          >
            Save settings
          </LoadingButton>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="subtitle1">MinerU conversion queue</Typography>
        <IconButton
          size="small"
          onClick={loadQueueStats}
          disabled={queueLoading}
          aria-label="Refresh queue status"
        >
          <RefreshRounded fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Platform-wide status of the asynchronous document conversion pipeline.
      </Typography>

      {queueError && (
        <Alert severity="error" onClose={() => setQueueError("")} sx={{ mb: 2 }}>
          {queueError}
        </Alert>
      )}

      {queueLoading ? (
        <CircularProgress size={20} />
      ) : queueStats ? (
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          {[
            { label: "Queued", value: queueStats.counts?.queued },
            { label: "Processing", value: queueStats.counts?.processing },
            { label: "Completed", value: queueStats.counts?.completed },
            { label: "Failed", value: queueStats.counts?.failed, error: true },
          ].map((stat) => (
            <Box key={stat.label}>
              <Typography
                variant="h6"
                color={stat.error && stat.value ? "error.main" : "text.primary"}
              >
                {stat.value ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stat.label}
              </Typography>
            </Box>
          ))}
          <Box>
            <Typography variant="h6">
              {queueStats.run?.running ? "Active" : "Idle"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              GPU worker
            </Typography>
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
};

export default DocumentProcessingSettings;
